import { debounce } from '@execonline-inc/rate-limiting.private';
import { milliseconds } from '@execonline-inc/time';
import { noop } from '@kofno/piper';
import { Task } from 'taskarian';
import { postToApi } from '../../Appy';
import { warnAndNotify } from '../../Honeybadger';
import { findLinkT } from '../../LinkyLinky';
import { Link } from '../../Resource/Types';
import SegmentStore from '../../SegmentStore';
import { SegmentReactionErrors } from './Types';

// Autosave failures were reported to Honeybadger until #9377, which swapped the
// call for a +console.log+ because the volume of timeouts was eating the monthly
// notification budget. That left us with no signal at all, and learners went on
// losing Action Plan answers behind a "Saving..." indicator for months.
//
// Report the first occurrence of each error kind per page load and count the
// rest into the console. One notification per kind per session keeps the
// timeout floods from recurring while still surfacing that a kind is happening.
const reportedErrorKinds = new Set<string>();

const reportAutoSaveError = (err: SegmentReactionErrors): void => {
  if (reportedErrorKinds.has(err.kind)) {
    console.log('SegmentReactions auto-save error (already reported)', err.kind, err);
    return;
  }

  reportedErrorKinds.add(err.kind);
  warnAndNotify('SegmentReactions auto-save error', err.kind, err);
};

export const autoSavingFormFields = debounce(
  (store: SegmentStore, onError: () => void = noop, onSuccess: () => void = noop) => {
    const fields = store.embeddedFormFieldOutputs.map((embeddedFormFieldOutput) => ({
      id: embeddedFormFieldOutput.id,
      value: embeddedFormFieldOutput.value,
    }));

    Task.succeed<SegmentReactionErrors, ReadonlyArray<Link>>(store.links)
      .andThen(findLinkT('results-create'))
      .andThen(postToApi({ results: fields }))
      .fork(
        (err) => {
          reportAutoSaveError(err);
          store.saveComplete();
          onError();
        },
        () => {
          // Order matters: fold the saved values into the resource BEFORE
          // retiring the saving state, so any render that follows already sees
          // a +payload.results+ that matches the server.
          store.recordSavedResults(fields);
          store.saveComplete();
          onSuccess();
        },
      );
  },
  milliseconds(200),
);
